import { forwardRef } from "react";
import { ReactComponent as CalendarIcon } from "@icons/calendar.svg";
import { parse, isValid } from "date-fns";
import { Input, InputProps } from "../../Input/Input";
import classNames from "classnames";

export interface DateInputProps extends InputProps {
  minDate?: Date;
  maxDate?: Date;
  locale?: string;
  timeProps?: InputProps;
}

const dateFormat = () => `yyyy/MM/dd`;

// eslint-disable-next-line default-param-last
export const formatDate = (date = "", currentValue: string) => {
  let currentDate = date;

  currentDate.replace(/.*\/$/g, (text: string) => (currentDate.length < currentValue.length ? currentDate.slice(0, -1) : text));

  if (currentDate.length > 6) {
    currentDate = currentDate.replace(/[^\d/]/g, "").replace(/(\d\d\d\d)(\d)/g, "$1");
  } else {
    currentDate = currentDate
      .replace(/(\d\d)(\d)/g, "$1/$2")
      .replace(/^(\d\/)$/g, "0$1")
      .replace(/\d\d$/g, (text: string) => (currentDate.length > currentValue.length && currentDate.length < 7 ? `${text}/` : text))
      .replace(/^(\d\d\/)(\d\/)$/g, "$10$2")
      .replace(/[^\d/]/g, "")
      .replace(/^\/(.*)/g, "$1");
  }

  return currentDate.replace(/^(.{0,10})(.*)$/g, "$1").replace(/\/\/$/g, "/");
};

const isDateWithinRange = (date: Date, start?: Date, end?: Date) =>
  isValid(date) && (start === undefined || date >= start) && (end === undefined || date <= end);

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ minDate, maxDate, locale = "en-gb", value = "", timeProps, onKeyDown, onChange, placeholder, ...rest }, ref) => {
    const currentValue = value as string;

    return (
      <div className="flex space-x-2 items-center">
        <div className={classNames(!!timeProps ? "w-2/3" : "w-full", !!timeProps && rest.helperText && "mt-[24px]")}>
          <Input
            {...rest}
            placeholder={placeholder}
            ref={ref}
            type="text"
            value={value}
            autoComplete="off"
            icon={<CalendarIcon className="pointer-events-none" />}
            onKeyDown={(e: any) => {
              const target = e.target as HTMLInputElement;

              if (e.key.includes("Enter")) {
                const date = parse(target.value, dateFormat(), new Date());

                if (!isDateWithinRange(date, minDate, maxDate)) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
              }

              onKeyDown?.(e);
            }}
            onChange={(e: any) => {
              e.target.value = formatDate(e.target.value, currentValue);
              onChange?.(e);
            }}
          />
        </div>
        {!!timeProps && (
          <div className={classNames("w-1/3", { "mt-[32px]": !!rest.label })}>
            <Input
              type="time"
              aria-label="date-picker-time"
              data-testid="date-picker-time"
              className="text-brand-purple"
              state={rest.state}
              {...timeProps}
            />
          </div>
        )}
      </div>
    );
  }
);
